import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { APP_DIALOG_EVENT, AppDialogRequest } from '../utils/appDialog';

interface CustomAlertData {
    message: string;
    title?: string;
    type?: 'info' | 'warning' | 'error' | 'success';
}

// Ekrandaki pencere: bilgi (alert) ya da utils/appDialog.ts'ten gelen onay / metin isteği.
// Aynı anda birden fazla gelirse sıraya girer (eskiden ikinci alert birincisini eziyordu).
type DialogItem = ({ kind: 'alert' } & CustomAlertData) | AppDialogRequest;

const CustomAlertModal: React.FC = () => {
    const [queue, setQueue] = useState<DialogItem[]>([]);
    const [promptValue, setPromptValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const current = queue[0] || null;

    useEffect(() => {
        // Original window.alert reference
        const originalAlert = window.alert;
        const push = (item: DialogItem) => setQueue(q => [...q, item]);

        // Custom alert interceptor
        const customAlert = (message?: any) => {
            const text = typeof message === 'string' ? message : (message?.toString() || '');
            if (!text.trim()) return;

            let type: 'info' | 'warning' | 'error' | 'success' = 'info';
            let title = 'KPSS Kapi Bilgilendirme';

            if (text.includes('⚠️') || text.includes('dikkat') || text.includes('Limit') || text.includes('saatleri') || text.includes('engellendi')) {
                type = 'warning';
                title = '⚠️ Sistem Uyarısı';
            } else if (text.includes('🎉') || text.includes('Tebrikler') || text.includes('başarıyla') || text.includes('onaylandı')) {
                type = 'success';
                title = '🎉 Harika!';
            } else if (text.includes('❌') || text.includes('Hata') || text.includes('başarısız') || text.includes('HATA')) {
                type = 'error';
                title = '❌ İşlem Başarısız';
            }

            push({ kind: 'alert', message: text, title, type });
        };

        // Override window.alert globally
        window.alert = customAlert;

        // Custom event listener for explicit triggers
        const handleCustomEvent = (e: Event) => {
            const customEvent = e as CustomEvent<CustomAlertData>;
            if (customEvent.detail) push({ kind: 'alert', ...customEvent.detail });
        };
        // Onay / metin istekleri (appConfirm / appPrompt)
        const handleDialogRequest = (e: Event) => {
            const detail = (e as CustomEvent<AppDialogRequest>).detail;
            if (detail) push(detail);
        };

        window.addEventListener('app-custom-alert', handleCustomEvent);
        window.addEventListener(APP_DIALOG_EVENT, handleDialogRequest);
        (window as any).__kpssDialogHostReady = true;

        return () => {
            window.alert = originalAlert;
            window.removeEventListener('app-custom-alert', handleCustomEvent);
            window.removeEventListener(APP_DIALOG_EVENT, handleDialogRequest);
            (window as any).__kpssDialogHostReady = false;
        };
    }, []);

    // Metin isteğinde varsayılan değeri doldur, alana odaklan
    useEffect(() => {
        if (current?.kind === 'prompt') {
            setPromptValue(current.options.defaultValue ?? '');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [current]);

    const close = (result: boolean) => {
        if (!current) return;
        if (current.kind === 'confirm') current.resolve(result);
        else if (current.kind === 'prompt') current.resolve(result ? promptValue : null);
        setQueue(q => q.slice(1));
    };

    // Klavye: Enter = onay, Esc = vazgeç
    useEffect(() => {
        if (!current) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.preventDefault(); close(current.kind === 'alert'); }
            else if (e.key === 'Enter' && current.kind !== 'prompt') { e.preventDefault(); close(true); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current, promptValue]);

    if (!current) return null;

    const isAlert = current.kind === 'alert';
    const options = isAlert ? null : current.options;
    const title = isAlert ? (current.title || 'KPSS Kapi Bilgilendirme') : (options?.title || (current.kind === 'prompt' ? 'Bilgi Gerekli' : 'Emin misin?'));
    const danger = !isAlert && !!options?.danger;

    return (
        <AnimatePresence>
            <div
                className="fixed inset-0 z-[999999] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in pointer-events-auto"
                onClick={(e) => { if (e.target === e.currentTarget && !isAlert) close(false); }}
            >
                <motion.div
                    key={queue.length}
                    initial={{ scale: 0.9, opacity: 0, y: 12 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 12 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 280 }}
                    role="dialog"
                    aria-modal="true"
                    className="bg-slate-900 border border-white/10 rounded-3xl p-6 max-w-md w-full max-h-full shadow-[0_0_60px_rgba(168,85,247,0.25)] flex flex-col items-center text-center space-y-4 relative overflow-hidden"
                >
                    {/* Glowing Background Ambience */}
                    <div className="absolute -top-16 -left-16 w-36 h-36 bg-purple-600/20 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-16 -right-16 w-36 h-36 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />

                    {/* Robot Avatar */}
                    <div className="w-16 h-16 shrink-0 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 p-0.5 shadow-xl flex items-center justify-center animate-bounce-subtle z-10">
                        <div className="w-full h-full bg-slate-950 rounded-[14px] flex items-center justify-center text-3xl">
                            {isAlert ? '🤖' : current.kind === 'prompt' ? '✍️' : '🤔'}
                        </div>
                    </div>

                    {/* Header Title */}
                    <h3 className="text-base font-black text-white tracking-wide z-10 flex items-center justify-center gap-2">
                        {title}
                    </h3>

                    {/* Message Box */}
                    <div className="text-xs md:text-sm text-slate-300 font-medium leading-relaxed whitespace-pre-wrap max-h-[55vh] overflow-y-auto px-3 scrollbar-thin scrollbar-thumb-purple-500/20 z-10 border-t border-b border-white/10 py-3.5 w-full bg-slate-950/40 rounded-2xl">
                        {current.message}
                    </div>

                    {current.kind === 'prompt' && (
                        <input
                            ref={inputRef}
                            value={promptValue}
                            onChange={(e) => setPromptValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); close(true); } }}
                            placeholder={current.options.placeholder}
                            className="w-full z-10 px-4 py-3 rounded-2xl bg-slate-950/70 border border-white/15 text-white text-sm font-semibold outline-none focus:border-purple-400"
                        />
                    )}

                    {isAlert ? (
                        /* Close Action Button */
                        <button
                            onClick={() => close(true)}
                            className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 active:scale-95 text-white text-xs md:text-sm font-extrabold rounded-2xl shadow-lg transition-all cursor-pointer z-10 border border-white/10 tracking-wide"
                        >
                            Tamam, Anladım
                        </button>
                    ) : (
                        <div className="w-full flex gap-3 z-10">
                            <button
                                onClick={() => close(false)}
                                className="flex-1 py-3 px-4 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 text-xs md:text-sm font-extrabold rounded-2xl transition-all cursor-pointer border border-white/10"
                            >
                                {options?.cancelText || 'Vazgeç'}
                            </button>
                            <button
                                onClick={() => close(true)}
                                className={`flex-1 py-3 px-4 active:scale-95 text-white text-xs md:text-sm font-extrabold rounded-2xl shadow-lg transition-all cursor-pointer border border-white/10 ${danger
                                    ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500'
                                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500'}`}
                            >
                                {options?.confirmText || (current.kind === 'prompt' ? 'Tamam' : 'Evet')}
                            </button>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default CustomAlertModal;
